import { Descendant } from 'slate';

import { ColorPalette } from '../components/_organisms/DesignerTabPane/AgentTab/AgentHooks';
import {
  CuripodElement,
  CuripodText,
  isCuripodElement,
  isCuripodText,
} from '../components/SlideComponents/slateUtil';
import { Slide, SlideElement } from '../components/SlideComponents/slide.types';

/**
 * Safely applies theme colors to slides by mapping from one color palette to another
 * Uses visitor pattern to transform all colors including text colors within Slate content
 * @param slides - Array of slides to theme
 * @param fromColorPalette - Source color palette to map from
 * @param toColorPalette - Target color palette to map to
 * @returns Array of themed slides with updated colors and background
 */
export function themeSlides({
  slides,
  fromColorPalette,
  toColorPalette,
}: {
  slides: Slide[];
  fromColorPalette: ColorPalette;
  toColorPalette: ColorPalette;
}): Slide[] {
  // Create a mapping from default colors to target colors with uppercase keys for case-insensitive lookup
  const colorMapping: Record<string, string> = {
    [fromColorPalette.dark.toUpperCase()]: toColorPalette.dark,
    [fromColorPalette.base.toUpperCase()]: toColorPalette.base,
    [fromColorPalette.light.toUpperCase()]: toColorPalette.light,
    [fromColorPalette.veryLight.toUpperCase()]: toColorPalette.veryLight,
    [fromColorPalette.fontColor.toUpperCase()]: toColorPalette.fontColor,
    [fromColorPalette.backgroundColor.toUpperCase()]: toColorPalette.backgroundColor,
    // These are non-standard colors that we use for some elements
    '#FFE3CC': toColorPalette.light,
  };

  // Helper function to map a color using the color mapping
  const mapColor = (color: string): string => {
    return colorMapping[color.toUpperCase()] || color;
  };

  // Deep clone the slides to avoid mutating the original
  const themedSlides: Slide[] = slides.map((slide: Slide) => {
    // Create background media using the backgroundColor from the palette
    const themedBackgroundMedia = {
      brightness: 0.5,
      fileId: toColorPalette.backgroundColor,
      fileMimeType: 'image/color' as const,
    };

    // Theme the existing elements with deep color mapping
    const themedExistingElements: SlideElement[] = slide.elements.map(
      (element: SlideElement) => {
        const themedElement: SlideElement = { ...element };

        // Map element-level colors array
        if (element.colors && Array.isArray(element.colors)) {
          themedElement.colors = element.colors.map(mapColor);
        }

        // Map text colors within Slate content
        if (element.type === 'text' && element.textValue) {
          try {
            const parsedSlate: CuripodElement[] = parseStringifiedSlateOrThrow(
              element.textValue,
            );

            // Transform Slate content using visitor pattern to map all colors
            const themedSlate: Descendant[] = transformSlateNodes(
              parsedSlate,
              (node: Descendant) => {
                if (isCuripodText(node)) {
                  const textNode = node as CuripodText;
                  return {
                    ...textNode,
                    color: textNode.color ? mapColor(textNode.color) : textNode.color,
                  };
                }

                if (isCuripodElement(node)) {
                  const elementNode = node as CuripodElement;
                  return {
                    ...elementNode,
                    color: elementNode.color
                      ? mapColor(elementNode.color)
                      : elementNode.color,
                  };
                }

                return node;
              },
            );

            themedElement.textValue = JSON.stringify(themedSlate);
          } catch (error) {
            // If parsing fails, keep original textValue
            console.warn('Failed to parse Slate content for theming:', error);
          }
        }

        return themedElement;
      },
    );

    return {
      ...slide,
      backgroundMedia: themedBackgroundMedia,
      elements: themedExistingElements,
    };
  });

  return themedSlides;
}

function parseStringifiedSlateOrThrow(slateString: string): CuripodElement[] {
  try {
    // Check if it is valid JSON
    const object = JSON.parse(slateString);
    return object; // Skipping zod validation
  } catch (error) {
    throw new Error(`Invalid stringified Slate: ${error}`);
  }
}

/**
 * Transform Slate nodes using a visitor function
 */
const transformSlateNodes = (
  nodes: Descendant[],
  visitor: (node: Descendant) => Descendant | Descendant[],
): Descendant[] => {
  const results: Descendant[] = [];

  for (const node of nodes) {
    const transformed = visitor(node);

    if (Array.isArray(transformed)) {
      // Handle case where visitor returns multiple nodes
      results.push(...transformed);
    } else if (isCuripodElement(transformed)) {
      const transformedChildren = transformSlateNodes(transformed.children, visitor);
      results.push({
        ...transformed,
        children: transformedChildren,
      });
    } else {
      results.push(transformed);
    }
  }

  return results;
};
